import { useEffect, useRef } from "react";

import { getElementIdFromHash } from "@pm2/common-utils";
import { pm2SitemapStore } from "@pm2/sitemap-utils";
import { isScrollingToNote } from "@pm2/ui-layout";
import { useLocation } from "@provider/navigation";
import { useNotifier } from "@provider/notifier";
import { reduceNodeOffset } from "@utils/common";

/**
 * when the content page has initially been mounted, then scroll directly
 * to the requested content anchor (if one is present)
 */
export function useScrollAnchorIntoView(): void {
  const hasRenderedFirstPage = pm2SitemapStore.useSelector(
    (state) => state.hasRenderedFirstPage,
  );
  const hasAppliedAnchorForPage = useRef<string | undefined>(undefined);
  const notifier = useNotifier();
  const location = useLocation();

  useEffect(() => {
    if (
      !hasRenderedFirstPage ||
      hasAppliedAnchorForPage.current === `${location.asPath}${location.hash}`
    ) {
      return;
    }

    hasAppliedAnchorForPage.current = `${location.asPath}${location.hash}`;

    // extract desired element id from the URL
    const elementId = getElementIdFromHash(window.location.hash);
    const anchorId = location.hash.replace("#", "").replace(/&.*$/, "");

    if (!elementId && !anchorId) {
      return;
    }

    const initialScroll = window.scrollY;

    // attempt to scroll to the desired element
    setTimeout(() => {
      const hasScrolled = Math.abs(window.scrollY - initialScroll) > 100;

      // In case a hash changed occurred due to a note being highlighted, then
      // do bail out as scrolling will be handled elsewhere.
      if (isScrollingToNote || hasScrolled) {
        return;
      }

      const element =
        document.querySelector<HTMLElement>(`[data-node-id="${elementId}"]`) ??
        document.querySelector<HTMLElement>(`[name="${anchorId}"]`) ??
        document.getElementById(anchorId);

      if (element) {
        // if we found the element, then keep on calculating it's offset until
        // it stops moving downwards for x consecutive milliseconds
        window.scrollTo({
          top: reduceNodeOffset(element).top - 100,
          behavior: "smooth",
        });
      } else {
        // if we were unable to find the element, then embed a snackbar on top
        // of the site

        notifier.notify({
          type: "error",
          body: "Linket er ikke længere gyldigt. Indholdet, du forsøger at tilgå er enten flyttet eller slettet.",
        });
      }
    }, 350);
  }, [
    notifier,
    location.asPath,
    hasRenderedFirstPage,
    location.hash,
    location,
  ]);
}
